import { get } from '@/utils'

/*
 * The internal format that describes current sorting, pagination and filtering states of the table:
 *
 * {
 *   page: 1,
 *   perPage: 10,
 *   sortBy: [
 *     { field: 'email', dir: 'options.ascDirValue || options.descDirValue' }
 *   ],
 *   filters: {
 *     email: [
 *       { value: 'gmail.co.uk', modifier: 'contains' },
 *       { value: 'gmail.com', modifier: 'contains' }
 *     ]
 *   }
 * }
 */

/**
 * Self-explaining
 */
export const config = {
  prefix: 't_', // that's using as query string params prefix for the case when there are two (or more) tables at the same page
  sortParam: 'sort',
  sortFieldsDelimiter: ',',
  sortDirectionDelimiter: ':',
  ascDirValue: 'asc',
  descDirValue: 'desc',
  pageParam: 'p',
  perPageParam: 'pp',
  filterModifierDelimiter: ':',
  multipleValueFilterModifiers: ['in', 'not_in'],
  multipleValueFilterValuesDelimiter: ','
}

const coerceType = (value, type) => {
  switch (type) {
    case Number:
      return Number(String(value).replace(',', '.'))
    case Boolean:
      return value === 'true'
    default: return value
  }
}

const parseFilterCondition = (param, value, options) => {
  if (!param || !value) return { field: null, modifier: null, value: null }
  const [first, second] = param.split(options.filterModifierDelimiter)
  const field = !second ? first : second
  const modifier = !second ? 'equals' : first
  const dataType = get(options.filters, `${field}.dataType`, null)
  const multipleValues = options.multipleValueFilterModifiers.includes(modifier)
  const values = multipleValues ? value.split(options.multipleValueFilterValuesDelimiter) : [value]
  const valuesCoerced = values.map(val => coerceType(val, dataType))
  return {
    field,
    modifier,
    value: multipleValues ? valuesCoerced : valuesCoerced[0]
  }
}

export const buildFilterCondition = (field, modifier, value, options) => {
  const multipleValues = options.multipleValueFilterModifiers.includes(modifier)
  const isArr = Array.isArray(value)
  if (isArr !== multipleValues) throw new Error(`DataTableWrapper: filer "${field}" with modifier "${modifier}" got unexpected value "${value}"`)
  return {
    key: modifier === 'equals' ? field : `${modifier}${options.filterModifierDelimiter}${field}`,
    value: isArr ? value.join(options.multipleValueFilterValuesDelimiter) : value
  }
}

/**
 * Converts the route's query to the internal state
 * @param {Object} query
 * @param {Object} options
 */
export const queryToState = (query, options) => {
  return Object.entries({ ...query }).reduce((acc, [key, value]) => {
    if (!value || key.indexOf(options.prefix) !== 0) return acc
    const param = key.substring(options.prefix.length)
    switch (param) {
      case options.sortParam:
        acc.sortBy = value.split(options.sortFieldsDelimiter).map(fieldAndDir => {
          const [sortField, sortDirection] = fieldAndDir.split(options.sortDirectionDelimiter)
          return {
            field: sortField,
            dir: sortDirection
          }
        })
        break
      case options.pageParam:
        acc.page = Number(value)
        break
      case options.perPageParam:
        acc.perPage = Number(value)
        break
      case param:
        if (!acc.filters) acc.filters = {}
        // eslint-disable-next-line
        const condition = parseFilterCondition(param, value, options)
        if (!condition.field) acc.filters = {}
        if (!acc.filters[condition.field]) acc.filters[condition.field] = {}
        acc.filters[condition.field][condition.modifier] = condition.value
        break
    }
    return acc
  }, {})
}

/**
 * Converts the internal state to the route's query
 * @param {Object} state
 * @param {Object} options
 * @param initialSortBy
 */
export const stateToQuery = (state, options, initialSortBy) => {
  const query = Object.entries({ ...state }).reduce((acc, [key, value]) => {
    switch (key) {
      case 'page':
        acc[`${options.prefix}${options.pageParam}`] = value
        break
      case 'perPage':
        acc[`${options.prefix}${options.perPageParam}`] = value
        break
      case 'sortBy':
        // eslint-disable-next-line
        const sortBy = !value.length ? initialSortBy : value
        if (sortBy.length) {
          acc[`${options.prefix}${options.sortParam}`] = sortBy.map(({ field, dir }) => {
            const dirValue = dir === 'desc' ? options.descDirValue : options.ascDirValue
            return `${field}${options.sortDirectionDelimiter}${dirValue}`
          }).join(options.sortFieldsDelimiter)
        }
        break
      case 'filters':
        acc = {
          ...acc,
          ...Object.entries(value).reduce((filters, [field, conditions]) => {
            Object.entries(conditions).forEach(([modifier, val]) => {
              const { key, value } = buildFilterCondition(field, modifier, val, options)
              filters[`${options.prefix}${key}`] = value
            })
            return filters
          }, {})
        }
        break
    }
    return acc
  }, {})
  return query
}

/**
 * Converts the table options object (val) emitted by Vuetify data table to the internal state
 * @param {Object} val
 * @param {Object} options
 */
export const tableOptionsToState = (val, options) => {
  return Object.entries({ ...val }).reduce((acc, [key, value]) => {
    switch (key) {
      case 'page':
        acc.page = value
        break
      case 'itemsPerPage':
        acc.perPage = value
        break
      case 'sortBy':
        acc.sortBy = value.map((field, idx) => {
          return { field, dir: !val.sortDesc[idx] ? options.ascDirValue : options.descDirValue }
        })
        break
    }
    return acc
  }, {})
}

/**
 * Converts the internal state to the Vuetify data table options object
 * @param {Object} state
 * @param {Object} options
 */
export const stateToTableOptions = (state, options) => {
  return Object.entries({ ...state }).reduce((acc, [key, value]) => {
    switch (key) {
      case 'page':
        acc.page = value
        break
      case 'perPage':
        acc.itemsPerPage = value
        break
      case 'sortBy':
        value.forEach(({ field, dir }, idx) => {
          acc.sortBy[idx] = field
          acc.sortDesc[idx] = dir === options.descDirValue
        })
        break
    }
    return acc
  }, { sortBy: [], sortDesc: [] })
}

/**
 * Validates the data source configuration
 * @param {Object} options
 */
export const dataSourceOptionsValidator = options => {
  switch (options.type) {
    case 'local':
    case 'custom':
    case 'PrismaGQL':
    case 'AxisREST':
      return true
    case 'AxisGQL':
      if (!options.query) throw new Error('DataTableWrapper: AxisGQL data source type requires the "query" option to be defined.')
      return true
    default: throw new Error('DataTableWrapper: unknown data source type.')
  }
}
